import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import type { StepProps } from '../../../types/enrollment.types';

export default function ConsentStep({
  formData,
  errors,
  onCheckboxChange,
}: StepProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t('step.consent')}
      </Typography>

      {/* Terms of Service - Required */}
      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.consentTerms}
              onChange={onCheckboxChange('consentTerms')}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
            />
          }
          label={
            <Box>
              <Typography fontWeight="bold">
                {t('consent.terms')}
                <Typography component="span" color="error"> *</Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('consent.terms.text')}
              </Typography>
            </Box>
          }
        />
        {errors.consentTerms && (
          <Typography color="error" variant="caption" sx={{ ml: 4 }}>
            {errors.consentTerms}
          </Typography>
        )}
      </Paper>

      {/* Privacy Policy - Required */}
      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.consentPrivacy}
              onChange={onCheckboxChange('consentPrivacy')}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
            />
          }
          label={
            <Box>
              <Typography fontWeight="bold">
                {t('consent.privacy')}
                <Typography component="span" color="error"> *</Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('consent.privacy.text')}
              </Typography>
            </Box>
          }
        />
        {errors.consentPrivacy && (
          <Typography color="error" variant="caption" sx={{ ml: 4 }}>
            {errors.consentPrivacy}
          </Typography>
        )}
      </Paper>

      {/* Health Disclaimer - Required */}
      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.consentHealth}
              onChange={onCheckboxChange('consentHealth')}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
            />
          }
          label={
            <Box>
              <Typography fontWeight="bold">
                {t('consent.health')}
                <Typography component="span" color="error"> *</Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('consent.health.text')}
              </Typography>
            </Box>
          }
        />
        {errors.consentHealth && (
          <Typography color="error" variant="caption" sx={{ ml: 4 }}>
            {errors.consentHealth}
          </Typography>
        )}
      </Paper>

      {/* Marketing - Optional */}
      <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'white' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.consentMarketing}
              onChange={onCheckboxChange('consentMarketing')}
              sx={{
                color: 'white',
                '&.Mui-checked': { color: 'white' },
                '& .MuiSvgIcon-root': { fontSize: 28 },
              }}
            />
          }
          label={
            <Box>
              <Typography fontWeight="bold">{t('consent.marketing')}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('consent.marketing.text')}
              </Typography>
            </Box>
          }
        />
      </Paper>
    </Box>
  );
}
